import {
  formatConfig,
  getSlotPayload
} from '@maxpreps/shared/functions/utils/adUtils';
import {
  AppType,
  DeviceType
} from '@maxpreps/shared/functions/utils/requestContextUtils';
import { IPageConfig, IProjectConfig } from '@maxpreps/shared/models/metaTypes';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsAdmin from '../../hooks/useIsAdmin';
import { IBidBarrelStore, IGlobalStore, PageState } from '../reduxTypes';
import { BidBarrelStoreAction } from './bidBarrelReducer';

export const useBidBarrelStore = (): IBidBarrelStore =>
  useSelector<IGlobalStore, IBidBarrelStore>(s => s.bidbarrel);

interface IInitBidBarrelConfig {
  projectConfig: IProjectConfig;
  pageConfig: IPageConfig;
  deviceType: DeviceType;
  appType: AppType;
  pageViewGuid: string;
  contextDataPoints: any;
}

export type ISlotPayload =
  | string
  | [
      string,
      number,
      Record<'pos', string | undefined>,
      Record<'outOfPage', any | undefined>
    ];

interface IBidBarrelAPIInitializeConfig {
  [x: string]: any;
}
declare namespace BidBarrel {
  const on: (
    eventName: string,
    callback: (data: any) => any,
    once: boolean
  ) => void;
  const auction: (units: ISlotPayload[]) => void;
  const destroySlots: (units: ISlotPayload[]) => void;
  const initialize: (config: IBidBarrelAPIInitializeConfig) => void;
  const setConfig: (config: IBidBarrelAPIInitializeConfig) => void;
}

const useManagePageState = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { pageState } = useBidBarrelStore();

  useEffect(() => {
    const handleRouteStart = () => {
      dispatch({
        type: BidBarrelStoreAction.UpdatePageState,
        payload: PageState.Transitioning
      });
    };

    const routeChangeComplete = () => {
      dispatch({
        type: BidBarrelStoreAction.UpdatePageState,
        payload: PageState.Transitioned
      });
    };

    router.events.on('routeChangeStart', handleRouteStart);
    router.events.on('routeChangeComplete', routeChangeComplete);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleRouteStart);
      router.events.off('routeChangeComplete', routeChangeComplete);
    };
  }, [dispatch, router.events]);

  // kick it all off
  useEffect(() => {
    if (pageState === PageState.Prehydrated) {
      dispatch({
        type: BidBarrelStoreAction.UpdatePageState,
        payload: PageState.Hydrated
      });
    }
  }, [pageState, dispatch]);
};

const isBidBarrelAuctionCriteriaMet = (
  pageState,
  isInitialized,
  isEnabled,
  slots
) =>
  (pageState === PageState.Transitioned || pageState === PageState.Hydrated) &&
  isInitialized &&
  isEnabled &&
  !!slots?.length;

const performAuction = slots => {
  //console.log('Perform auction');

  const payload = getSlotPayload(slots);

  BidBarrel.destroySlots(payload);
  BidBarrel.auction(payload);
};

const useInitializeBidBarrel = (formattedConfig, isEnabled = false) => {
  const { isInitialized, pageState } = useBidBarrelStore();
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      !isEnabled ||
      pageState !== PageState.Hydrated ||
      isInitialized ||
      !BidBarrel
    ) {
      return;
    }

    // only fired once so we don't need an off
    BidBarrel.on(
      'initialize',
      () => dispatch({ type: BidBarrelStoreAction.SetInitialized }),
      true
    );

    BidBarrel.initialize(formattedConfig);
  }, [dispatch, isInitialized, formattedConfig, pageState]);
};

const isBidBarrelEnabled = (
  appType: AppType,
  isAdIntegrationEnabled: boolean,
  isAdmin: boolean,
  router
) => {
  const isEnabled =
    (appType ?? AppType.Website) === AppType.Website &&
    !isAdmin &&
    (router?.query.adsenabled ?? '1') === '1' &&
    isAdIntegrationEnabled;

  return isEnabled;
};

/**
 * Main hook to manage ad auctions through the BidBarrel API
 * @param config
 */
export const useBidBarrel = (config: IInitBidBarrelConfig) => {
  const isAdmin = useIsAdmin();
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    isInitialized,
    slots,
    auctionSlots,
    pageState,
    isAutomaticPageAuctionComplete
  } = useBidBarrelStore();

  const {
    projectConfig,
    pageConfig,
    appType,
    contextDataPoints,
    pageViewGuid,
    deviceType
  } = config;

  // This was being regenerated on every render (causing infinite loops) until I defined contextDataPoints with useMemo too
  const formattedConfig = useMemo(
    () =>
      formatConfig(
        projectConfig?.projectMetaData,
        pageConfig?.pageMetaData,
        contextDataPoints,
        pageViewGuid,
        deviceType,
        isAdmin
      ),
    [
      projectConfig?.projectMetaData,
      pageConfig?.pageMetaData,
      contextDataPoints,
      pageViewGuid,
      deviceType,
      isAdmin
    ]
  );

  // Setup listeners to know when to start auctions on CSR
  useManagePageState();

  // Get the 'enabled' state of BB updated
  const isEnabled = isBidBarrelEnabled(
    appType,
    pageConfig?.isAdIntegrationEnabled ?? true,
    isAdmin,
    router
  );

  // Get BB initialized
  useInitializeBidBarrel(formattedConfig, isEnabled);

  /**
   * Anytime the config is changed let bidbarrel to know about it
   */
  useEffect(() => {
    if (isInitialized) {
      BidBarrel.setConfig(formattedConfig);
    }
  }, [isInitialized, formattedConfig]);

  /**
   * on transition clear all slots
   */
  useEffect(() => {
    if (isInitialized && pageState === PageState.Transitioning) {
      BidBarrel.destroySlots(getSlotPayload(slots));
    }
  }, [pageState, dispatch, isInitialized, slots]);

  /**
   * Auctions for SSR and CSR pageload
   */
  useEffect(() => {
    if (
      !isBidBarrelAuctionCriteriaMet(pageState, isInitialized, isEnabled, slots)
    ) {
      return;
    }

    // don't auto-auction twice on a single pageview
    if (isAutomaticPageAuctionComplete) {
      return;
    }

    performAuction(slots);

    dispatch({ type: BidBarrelStoreAction.AutomaticPageAuctionComplete });
  }, [
    pageState,
    isInitialized,
    isEnabled,
    slots,
    dispatch,
    isAutomaticPageAuctionComplete
  ]);

  /**
   * Auctions for anything piped in through the 'auction' workflow
   */
  // uwc-debug
  useEffect(() => {
    if (
      !isBidBarrelAuctionCriteriaMet(
        pageState,
        isInitialized,
        isEnabled,
        auctionSlots
      )
    ) {
      return;
    }

    performAuction(auctionSlots);

    dispatch({
      type: BidBarrelStoreAction.ClearAuctionSlots,
      payload: auctionSlots
    });
  }, [pageState, isInitialized, isEnabled, auctionSlots, dispatch]);
};
